import { useLayoutEffect, useRef, useState } from 'react'
import classnames from 'classnames'

import { Tooltip } from '../Tooltip/Tooltip'
import { formatFolderPath } from './utils'

const measureCanvas = document.createElement('canvas')
const measureContext = measureCanvas.getContext('2d')

const measureTextWidth = (text: string, font: string): number => {
  if (!measureContext) {
    return 0
  }
  measureContext.font = font
  return measureContext.measureText(text).width
}

const getElementFont = (el: HTMLElement): string => {
  const style = window.getComputedStyle(el)
  return style.font || `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
}

interface FolderPathLabelProps {
  segments: string[]
  className?: string
}

export const FolderPathLabel = ({ segments, className }: FolderPathLabelProps) => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const [maxSegments, setMaxSegments] = useState<number>(segments.length)

  const segmentsKey = segments.join('/')

  useLayoutEffect(() => {
    const containerElement = containerRef.current
    if (!containerElement) {
      return
    }

    const fit = () => {
      const width = containerElement.clientWidth - 4
      if (width <= 0) {
        return
      }
      const font = getElementFont(containerElement)
      const allSegments = segmentsKey.split('/')
      const candidateCounts = Array.from({ length: allSegments.length }, (_, i) => allSegments.length - i)
      const fittingCount = candidateCounts.find((count) => measureTextWidth(formatFolderPath(allSegments, count).display, font) <= width)
      setMaxSegments(fittingCount ?? 1)
    }

    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(containerElement)
    return () => observer.disconnect()
  }, [segmentsKey])

  const { display, full } = formatFolderPath(segments, maxSegments)

  return (
    <span ref={containerRef} className={classnames('text-truncate', className)}>
      <Tooltip title={full} placement='top'>
        {display}
      </Tooltip>
    </span>
  )
}
